import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel,
    MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableFooter,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MerchReportService } from '../../services/merchReport.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { IStyle } from '../../interfaces/garments/style.interface';
import { ISampleTracker, ISampleTrackerFilters } from '../../interfaces/garments/merchReport.interface';
import { SampleStatus, SampleType } from '../../interfaces/garments/sampleRequest.interface';
import { BuyerAutocomplete } from './BuyerAutocomplete';
import { StyleAutocomplete } from './StyleAutocomplete';
import { ALL_SAMPLE_STATUSES } from './sampleRequestTransitions';
import { SAMPLE_TYPES } from './sampleTypeOptions';
import { money, triggerDownload } from '../statutoryRegisters/shared';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const STATUS_COLOR: Record<SampleStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
    REQUESTED: 'default',
    IN_WORK: 'info',
    SENT: 'primary',
    APPROVED: 'success',
    REJECTED: 'error',
    RESUBMIT_REQUIRED: 'warning',
    RESUBMITTED: 'default',
    CANCELLED: 'default',
};

/**
 * Sample tracker report (garments-merchandising T11, spec
 * `.superpowers/sdd/2026-09-09-garments-merchandising/t11-brief.md`). Filter bar over
 * `MerchandisingReportController#getSampleTracker`; renders one row per sample request/round plus
 * a status-total footer section (subtotal rows). Export XLSX streams the same filters through
 * `downloadSampleTrackerXlsx` and hands the blob to `triggerDownload` (G21).
 */
export function SampleTrackerReport() {
    const toast = useToast();

    const [report, setReport] = useState<ISampleTracker | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const [buyerFilter, setBuyerFilter] = useState<IBuyer | null>(null);
    const [styleFilter, setStyleFilter] = useState<IStyle | null>(null);
    const [status, setStatus] = useState<SampleStatus | ''>('');
    const [sampleType, setSampleType] = useState<SampleType | ''>('');
    const [requestedFrom, setRequestedFrom] = useState('');
    const [requestedTo, setRequestedTo] = useState('');
    const [overdueOnly, setOverdueOnly] = useState(false);

    const buildFilters = useCallback((): ISampleTrackerFilters => ({
        buyerId: buyerFilter?.id || undefined,
        styleId: styleFilter?.id || undefined,
        status: status || undefined,
        sampleType: sampleType || undefined,
        requestedFrom: requestedFrom || undefined,
        requestedTo: requestedTo || undefined,
        overdueOnly,
    }), [buyerFilter, styleFilter, status, sampleType, requestedFrom, requestedTo, overdueOnly]);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setLoading(true);
        try {
            const data = await MerchReportService.getSampleTracker(institutionId, buildFilters());
            setReport(data);
        } catch (e) {
            toast.showApiError(e, 'Failed to load sample tracker');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildFilters]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearFilters = () => {
        setBuyerFilter(null);
        setStyleFilter(null);
        setStatus('');
        setSampleType('');
        setRequestedFrom('');
        setRequestedTo('');
        setOverdueOnly(false);
    };

    const handleExport = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setDownloading(true);
        try {
            const blob = await MerchReportService.downloadSampleTrackerXlsx(institutionId, buildFilters());
            triggerDownload(blob, `sample-tracker-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Failed to export sample tracker');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.sampleTracker.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Sample Tracker</Typography>
                <Button
                    data-help="merchandising.sampleTracker.export"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleExport} disabled={!report || downloading}
                >
                    Export XLSX
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.sampleTracker.filter">
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                    <Box sx={{ flex: '1 1 220px', maxWidth: 340 }}>
                        <BuyerAutocomplete value={buyerFilter} onChange={b => { setBuyerFilter(b); setStyleFilter(null); }} label="Buyer" size="small" />
                    </Box>
                    <Box sx={{ flex: '1 1 220px', maxWidth: 340 }}>
                        <StyleAutocomplete value={styleFilter} onChange={setStyleFilter} label="Style" size="small" buyerId={buyerFilter?.id} />
                    </Box>
                    <FormControl data-help="merchandising.sampleTracker.type" size="small" sx={{ flex: '1 1 150px', maxWidth: 270 }}>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={sampleType} onChange={e => setSampleType(e.target.value as SampleType | '')}>
                            <MenuItem value="">All</MenuItem>
                            {SAMPLE_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl data-help="merchandising.sampleTracker.statusFilter" size="small" sx={{ flex: '1 1 170px', maxWidth: 290 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={status} onChange={e => setStatus(e.target.value as SampleStatus | '')}>
                            <MenuItem value="">All</MenuItem>
                            {ALL_SAMPLE_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        data-help="merchandising.sampleTracker.from" size="small" label="Requested From" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        value={requestedFrom} onChange={e => setRequestedFrom(e.target.value)}
                    />
                    <TextField
                        data-help="merchandising.sampleTracker.to" size="small" label="Requested To" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        value={requestedTo} onChange={e => setRequestedTo(e.target.value)}
                    />
                    <FormControlLabel
                        data-help="merchandising.sampleTracker.overdueOnly"
                        control={
                            <Checkbox
                                checked={overdueOnly}
                                onChange={e => setOverdueOnly(e.target.checked)}
                                inputProps={{ 'aria-label': 'Overdue only' }}
                            />
                        }
                        label="Overdue only"
                    />
                    <Button data-help="merchandising.sampleTracker.apply" variant="outlined" size="small" onClick={load} disabled={loading}>Apply</Button>
                    <Button data-help="merchandising.sampleTracker.clear" size="small" onClick={clearFilters}>Clear</Button>
                </Stack>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            )}

            {!loading && report && (
                <>
                    {report.caveats.map((c, i) => (
                        <Alert key={i} severity="info" sx={{ mb: 1 }}>{c}</Alert>
                    ))}

                    <TableContainer component={Paper} data-testid="sample-tracker-table" data-help-section="merchandising.sampleTracker.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="merchandising.sampleTracker.col.buyer">Buyer</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.style">Style</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.type">Type</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.round">Round</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.status">Status</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.requested">Requested</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.due">Due</TableCell>
                                    <TableCell align="right" data-help="merchandising.sampleTracker.col.age">Age (d)</TableCell>
                                    <TableCell data-help="merchandising.sampleTracker.col.courier">Courier</TableCell>
                                    <TableCell align="right" data-help="merchandising.sampleTracker.col.cost">Cost</TableCell>
                                    <TableCell align="right" data-help="merchandising.sampleTracker.col.hours">Hours</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4 }}>
                                                No sample requests match this filter.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.rows.map(row => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.buyerName}</TableCell>
                                        <TableCell>{row.styleName}</TableCell>
                                        <TableCell>{enumLabel(row.sampleType)}</TableCell>
                                        <TableCell>{row.roundNo}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Chip size="small" label={enumLabel(row.status)} color={STATUS_COLOR[row.status]} />
                                                {row.overdue && <Chip size="small" label="Overdue" color="error" variant="outlined" />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{formatDate(row.requestedDate)}</TableCell>
                                        <TableCell>{row.dueDate ? formatDate(row.dueDate) : '—'}</TableCell>
                                        <TableCell align="right">{row.ageInDays ?? '—'}</TableCell>
                                        <TableCell>{row.courierName ?? '—'}</TableCell>
                                        <TableCell align="right">{money(row.sampleCost)}</TableCell>
                                        <TableCell align="right">{row.laborHours ?? '—'}</TableCell>
                                    </TableRow>
                                ))}

                                {report.statusTotals.map(st => (
                                    <TableRow
                                        key={`subtotal-${st.status}`}
                                        data-testid={`sample-tracker-subtotal-${st.status}`}
                                        sx={{ '& td': { fontWeight: 'bold', bgcolor: 'action.hover' } }}
                                    >
                                        <TableCell colSpan={4}>Subtotal — {enumLabel(st.status)}</TableCell>
                                        <TableCell colSpan={7} align="right">{st.count}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow
                                    data-testid="sample-tracker-grand-total"
                                    sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}
                                >
                                    <TableCell colSpan={4}>Grand Total</TableCell>
                                    <TableCell colSpan={7} align="right">{report.rows.length}</TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}

export default SampleTrackerReport;
